import type { DealOfferSummary } from "@/components/marketplace/deal-offers-types";
import {
  formatCommercePrice,
  formatPriceDeltaPercent,
} from "@/components/marketplace/deal-offers-utils";
import { assessOfferPrice } from "@/lib/catalog-faq";

function usesAverageComparison(indicator: string | null): boolean {
  return indicator === "below_average" || indicator === "above_average";
}

function formatFaqDate(value: Date | string | null, formatDate: (value: Date) => string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatDate(date);
}

export function catalogPricingFaqModel(
  summary: DealOfferSummary,
  locale: string,
  formatDate: (value: Date) => string,
) {
  const currentPrice = formatCommercePrice(summary.bestOfferPriceTotal, summary.currency, locale);
  if (!currentPrice) return null;

  const assessment = assessOfferPrice(summary);
  const averageComparison = usesAverageComparison(assessment.comparisonIndicator);
  const comparisonValue = averageComparison
    ? summary.priceHistoryAverage
    : (summary.priceHistoryTypical ?? summary.priceHistoryAverage);

  return {
    assessment,
    comparisonKind: averageComparison ? "average" : "typical",
    comparisonPrice: formatCommercePrice(comparisonValue, summary.currency, locale) ?? currentPrice,
    currentPrice,
    dropPercent: formatPriceDeltaPercent(summary.dealDeltaPercent, locale) ?? "",
    lastCheckedDate: formatFaqDate(summary.lastCheckedAt, formatDate),
    maximumPrice: formatCommercePrice(
      summary.priceHistoryMax,
      summary.priceHistoryCurrency,
      locale,
    ),
    minimumPrice: formatCommercePrice(
      summary.priceHistoryMin,
      summary.priceHistoryCurrency,
      locale,
    ),
    rangeMaximum: formatCommercePrice(summary.allOffersTotalPriceMax, summary.currency, locale),
    rangeMinimum: formatCommercePrice(summary.allOffersTotalPriceMin, summary.currency, locale),
    shouldShowWishlist: [
      "insufficient_history",
      "expensive",
      "expensive_with_drop",
      "fair_price",
    ].includes(assessment.assessment),
  };
}

export type CatalogPricingFaqModel = NonNullable<ReturnType<typeof catalogPricingFaqModel>>;
